<script setup lang="ts">
import { ListFilterTypeExpression } from '@/enum/LinqExpressionType'
import { useNavigator } from '@/hooks'
import useAccountStore from '@/stores/modules/account'
import usePaginationStore from '@/stores/modules/pagination'
import CONSTANTS from '@/utils/constants/string-constants'
import { debounce } from 'lodash'
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
  type Ref
} from 'vue'
import { VVirtualScroll } from 'vuetify/components'
import {
  BaseButton,
  BaseSkeletonLoader,
  BaseSnackbar,
  BaseLoadMoreHandler as LoadMoreHandler
} from '../base'

const listProps = defineProps<{
  listName: string
  listType?: string
  listItemHeight: number
  noDeleteUnmount?: boolean
  initialFilterOptions?: Record<string, any> | null
  destroyOnUnmount?: boolean
  resetOnUnmount?: boolean
  hasSnackbar?: boolean
  noSelectAll?: boolean
  returnItemOnSelectAll?: boolean
  moduleName?: string
  unclickable?: boolean
  isDisabled?: boolean
  selectAllKey?: string
  ignoreInitialApiCall?: boolean
  shouldRefresh?: boolean
  externalLoading?: boolean
  listItemClass?: string
}>()
const emit = defineEmits(['apiCall'])

const slots = useSlots()
const navigator = useNavigator()
const accountStore = useAccountStore()
const paginationStore = usePaginationStore(listProps.listName)
const listContent: Ref = ref(null)
const virtualScrollContainer = ref()
const listHeight = ref()
const columns = ref()
const isListContentOverflowing = ref(false)
const EMPTY_FILTER_QUERY_MESSAGE = computed(() =>
  CONSTANTS.EMPTY_FILTER_QUERY_MESSAGE.split(CONSTANTS.SPLITTER)
)
// const EMPTY_LIST_MESSAGE = computed(() => CONSTANTS.EMPTY_LIST_MESSAGE.split(CONSTANTS.SPLITTER))
// const EMPTY_LIST_DIRECTIVE = computed(() =>
//   CONSTANTS.EMPTY_LIST_DIRECTIVE.split(CONSTANTS.SPLITTER)
// )
const EMPTY_LIST_ERROR_MESSAGE = computed(() =>
  CONSTANTS.EMPTY_LIST_ERROR_MESSAGE.split(CONSTANTS.SPLITTER)
)
const headerRightPadding = (() => {
  if (!slots['list-header']) return 0
  if (listProps.listItemHeight * (paginationStore.data?.length ?? 0) < listHeight.value) return 0
  const temporaryParent = document.createElement('div')
  const temporaryChild = document.createElement('div')
  temporaryParent.style.width = '40px'
  temporaryParent.style.height = '50px'
  temporaryParent.style.overflowY = 'scroll'
  temporaryParent.style.visibility = 'hidden'
  temporaryChild.style.width = '100%'
  temporaryChild.style.height = '100px'
  temporaryParent.appendChild(temporaryChild)
  document.body.appendChild(temporaryParent)
  const scrollbarWidth = temporaryParent.offsetWidth - temporaryParent.clientWidth
  document.body.removeChild(temporaryParent)
  return scrollbarWidth
})()
const loadedListLength = computed(() => paginationStore.size)
// const paddingBottomOnList = computed(() => {
//   if (listProps.listType === ListType.Assignment) return { paddingBottom: '56px' }
//   return { paddingBottom: '40px' }
// })
// const selectedItemsLabel = computed(() => {
//   if (!paginationStore.selectedItems?.length) return ''
//   if (paginationStore.selectedItems.length > 1) return listProps.moduleName
//   return listProps.moduleName?.slice(0, -1)
// })

const retryFailedRequest = () => {
  paginationStore.failed = false
  if (!paginationStore.data?.length) {
    paginationStore.loading = true
  }
  nextTick(() => {
    emit('apiCall', listProps.listName)
  })
}

const updateFilterOptions = (filters: any) => {
  paginationStore.filtersOptions = { ...paginationStore.filtersOptions, ...filters }
  Object.keys(filters).forEach((filterKey) => {
    const value = filters[filterKey]
    if (value !== null && value !== undefined) {
      paginationStore.filtersOptions = { ...paginationStore.filtersOptions, [filterKey]: value }
    } else if (filterKey in paginationStore.filtersOptions) {
      const { [filterKey]: value, ...rest } = paginationStore.filtersOptions
      paginationStore.filtersOptions = rest
    }
  })
}

const resetAndUpdateList = (value: any, oldValue: any) => {
  nextTick(() => {
    if (
      (listProps.initialFilterOptions &&
        !paginationStore.data?.length &&
        !paginationStore.noData) ||
      (!Object.keys(value).length && !Object.keys(oldValue).length)
    )
      return
    const { selectedItems, filtersOptions, isAllListItemsSelected } = paginationStore
    paginationStore.$reset()
    paginationStore.isAllListItemsSelected = isAllListItemsSelected
    paginationStore.selectedItems = selectedItems
    paginationStore.filtersOptions = filtersOptions
    emit('apiCall', listProps.listName)
  })
}

/**
 * refactor parent access
 */
// const handleResize = () => {
//   const listContainer = listContent.value.closest('.list-container')
//   if (listContainer) {
//     const listHeader = listContainer.querySelector('.list-header')
//     const listFilter = listContainer.querySelector('.list-filter')
//     const listFooter = listContainer.querySelector('.list-footer')
//     const heightToBeReduced =
//       parseInt(getComputedStyle(listContainer).paddingTop, 10) +
//       parseInt(getComputedStyle(listContainer).paddingBottom, 10) +
//       (listHeader?.offsetHeight || 0) +
//       (listFilter?.offsetHeight || 0) +
//       (listFooter?.offsetHeight || 0)
//     listHeight.value = listContainer.offsetHeight - heightToBeReduced
//   }
// }

const setInitialFilterOptions = () => {
  const initialFilters = listProps.initialFilterOptions?.[ListFilterTypeExpression.Filter]
  const existingFilters = paginationStore.filtersOptions[ListFilterTypeExpression.Filter]
  paginationStore.resetPaginationStateAndNotFilters()
  paginationStore.filtersOptions = {
    [ListFilterTypeExpression.Filter]: { ...existingFilters, ...initialFilters }
  }
}

const calculateAndSetNumberOfColumns = () => {
  const listHeader = document.querySelectorAll('.list-header .row > div')
  columns.value = Object.values(listHeader).map((header) => `${header.clientWidth}px`)
}

const updateScrollPosition = debounce(() => {
  const scrollAmount = virtualScrollContainer.value?.$el.scrollTop
  paginationStore.scrollPosition = { x: 0, y: scrollAmount }
}, 1000)

const checkSelectedAllListItems = () => {
  if (
    paginationStore.isAllListItemsSelected &&
    paginationStore.someItemsSelected &&
    paginationStore.isSelectedAllSnackbarItems
  ) {
    paginationStore.toggleSelectAllItemsOfList(
      listProps?.returnItemOnSelectAll,
      listProps.listType,
      listProps.selectAllKey
    )
  }
}

const scrollToLastSavedScrollPosition = () => {
  nextTick(() => {
    if (virtualScrollContainer.value) {
      virtualScrollContainer.value.$el.scrollTop = paginationStore.scrollPosition?.y
    }
  })
}

const handleSelectAllItemsInList = () => {
  if (paginationStore.isAllListItemsSelected) {
    paginationStore.toggleSelectAllItemsOfList(
      listProps?.returnItemOnSelectAll,
      listProps.listType,
      listProps.selectAllKey
    )
  } else {
    paginationStore.removeItemsInListFromSelectedItems(listProps.selectAllKey)
  }
}

watch(loadedListLength, (listLength, oldListLength): any => {
  if (listLength > oldListLength) {
    updateScrollPosition()
    checkSelectedAllListItems()
  }
})

watch(
  () => paginationStore.isSelectedAllSnackbarItems,
  () => {
    if (paginationStore.isSelectedAllSnackbarItems && !paginationStore.allItemsSelected) {
      paginationStore.isAllListItemsSelected = true
      paginationStore.toggleSelectAllItemsOfList(
        listProps?.returnItemOnSelectAll,
        listProps.listType,
        listProps.selectAllKey
      )
    } else if (!paginationStore.isSelectedAllSnackbarItems) {
      paginationStore.isAllListItemsSelected = false
      paginationStore.clearSelectedItems()
    }
  }
)
watch(
  () => paginationStore.someItemsSelected,
  () => {
    if (paginationStore.someItemsSelected && paginationStore.isSelectedAllSnackbarItems) {
      paginationStore.isSelectedAllSnackbarItems = false
    }
    if (paginationStore.someItemsSelected && paginationStore.isAllListItemsSelected) {
      paginationStore.isAllListItemsSelected = false
    }
  }
)
watch(
  () => paginationStore.allItemsSelected,
  () => {
    if (paginationStore.allItemsSelected) {
      paginationStore.isAllListItemsSelected = true
    }
  }
)

watch(() => paginationStore.filtersOptions, resetAndUpdateList, { flush: 'post' })

watch(
  () => virtualScrollContainer.value,
  () => {
    nextTick(() => {
      if (virtualScrollContainer.value) {
        const isListOverflowing = Boolean(
          virtualScrollContainer.value.$el.scrollHeight >
            virtualScrollContainer.value.$el.clientHeight
        )
        isListContentOverflowing.value = isListOverflowing
      }
    })
  }
)

onBeforeMount(() => {
  if (listProps.initialFilterOptions) {
    setInitialFilterOptions()
  }
  nextTick(() => {
    accountStore.profilePromise?.then(() => {
      if (!listProps.ignoreInitialApiCall) {
        emit('apiCall', listProps.listName)
      }
    })
  })
})

onMounted(() => {
  calculateAndSetNumberOfColumns()
  scrollToLastSavedScrollPosition()
})

onBeforeUnmount(() => {
  if (listProps.shouldRefresh) {
    paginationStore.resetPaginationStateAndNotFilters()
    return
  }
  if (listProps.resetOnUnmount) {
    paginationStore.resetPaginationState()
    return
  }
  if (!listProps.destroyOnUnmount) return
  if (listProps.noDeleteUnmount) {
    paginationStore.resetPaginationState()
  } else {
    paginationStore.resetAndDeletePaginationState()
  }
})
</script>
<template>
  <div
    :class="`${$attrs['is-filter-outside-list'] !== undefined ? '' : 'list-container'} infinite-list list-view transparent d-flex flex-column overflow-y--hidden`"
    :list-type="listType ?? ''"
    v-bind="$attrs"
  >
    <div v-if="$slots['list-filter']" class="list-filter">
      <slot
        name="list-filter"
        :update-filter-options="updateFilterOptions"
        :filter-options="paginationStore.filtersOptions"
      />
    </div>
    <div
      ref="listContent"
      :class="`list-content ${$attrs['is-filter-outside-list'] ? 'h-max-100' : ''} ${paginationStore.size ? 'overflow-y--auto' : ''}`"
    >
      <div
        v-if="$slots['list-header']"
        class="list-header caps-12 semibold secondary_text--text hide-selection-highlight bg-white"
        :style="{ paddingRight: `${isListContentOverflowing ? headerRightPadding : 0}px` }"
      >
        <slot name="list-header" :select-all-list-items-in-list="handleSelectAllItemsInList" />
      </div>
      <v-virtual-scroll
        ref="virtualScrollContainer"
        v-if="!externalLoading && !paginationStore.noData && paginationStore.data?.length"
        :class="{ 'list-disabled': isDisabled }"
        :bench="3"
        :items="paginationStore.data"
        :height="listHeight"
        :item-height="listItemHeight"
      >
        <template #default="{ item, index }: { item: any; index: number }">
          <div
            :key="item.id"
            :class="`list-item body-2 hide-selection-highlight ${listItemClass} ${!unclickable ? 'list-item-hover' : ''} ${isDisabled ? 'list-item-disabled' : ''}`"
            cols="12"
            :data-id="item.id"
          >
            <slot name="list-item" :list-item="item" :list-item-index="index" />
          </div>
          <LoadMoreHandler
            v-if="
              index == paginationStore.size - 1 &&
              paginationStore.size > 0 &&
              paginationStore.hasMoreData
            "
            :loaded-list-length="loadedListLength"
            :total-list-length="paginationStore.totalRowCount"
            @load-more="$emit('apiCall', listName)"
          >
            <slot name="list-load-more-loader" />
          </LoadMoreHandler>
        </template>
      </v-virtual-scroll>
    </div>
    <div v-if="$slots['list-footer']" class="list-footer-bottom list-footer">
      <slot name="list-footer" />
    </div>
    <div
      v-if="!paginationStore.loading && !externalLoading && !paginationStore.data?.length"
      class="zero-state-container h-100 white py-9 px-5"
    >
      <slot v-if="paginationStore.totalRowCount <= 0 && paginationStore.noData" name="zero-state">
        <p v-if="paginationStore.hasActiveFilters">
          {{ EMPTY_FILTER_QUERY_MESSAGE[0] }}{{ 'items' }}{{ EMPTY_FILTER_QUERY_MESSAGE[1] }}
        </p>
      </slot>
      <div
        v-else-if="paginationStore.failed"
        class="d-flex flex-column h-100 justify-center align-center"
      >
        <v-icon width="32" max-height="27" icon="mdi-alert-circle" color="danger_700" />
        <p class="message text-center my-4">
          {{ EMPTY_LIST_ERROR_MESSAGE[0] }}{{ moduleName }}{{ EMPTY_LIST_ERROR_MESSAGE[1] }}
        </p>
        <BaseButton
          left-icon="mdi-reload"
          variant="outlined"
          class="bg-white text-danger_700"
          @click="retryFailedRequest"
        >
          {{ CONSTANTS.RETRY }}
        </BaseButton>
      </div>
    </div>
    <div
      v-if="(paginationStore.loading && !paginationStore.offset) || externalLoading"
      class="list-skeleton overflow-y--auto flex-grow-1 h-100 white"
    >
      <slot name="list-skeleton-loader">
        <div class="w-100 d-flex align-center px-5">
          <BaseSkeletonLoader
            v-for="(_, index) in columns"
            :key="index"
            :count="6"
            type="table-cell"
            :height="listItemHeight"
            :column-widths="columns"
          />
        </div>
      </slot>
    </div>
    <BaseSnackbar
      height="auto"
      v-model="paginationStore.showFailedSnackbar"
      :bottom="listType === 'table'"
      color="white"
    >
      <p class="black--text d-inline-flex mb-0 align-center px-3 py-5">
        <!-- <v-img src="@/assets/failed-request-icon.svg" class="mr-3" width="20" max-height="17" /> -->
        {{ EMPTY_LIST_ERROR_MESSAGE[0]
        }}{{ moduleName ?? navigator.getCurrentRoute().name?.toLowerCase()
        }}{{ EMPTY_LIST_ERROR_MESSAGE[1] }}
        <BaseButton
          class="px-0 py-1 transparent brand_light--text ml-4 body-1"
          height="auto"
          rounded
          :ripple="false"
          @click="retryFailedRequest"
        >
          {{ CONSTANTS.RETRY }}
        </BaseButton>
      </p>
    </BaseSnackbar>
  </div>
</template>
<style scoped lang="scss">
.list-header {
  position: sticky;
  top: 0;
  z-index: 1;
}
.list-footer-bottom {
  position: absolute;
  width: 100%;
  bottom: 0;
  background-color: #ffffff;
  z-index: 1;
}
.list-disabled {
  pointer-events: none !important;
  background: linear-gradient(90deg, #ffffff, #dddddd, #ffffff);
  background-size: 400% 400%;
  animation: gradient 3s ease infinite;
}
.list-item-disabled {
  cursor: not-allowed !important;
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 0%;
  }
}
</style>
